import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createWebhook,
  deleteWebhook,
  getWebhookDelivery,
  resendWebhookDelivery,
  rotateWebhookSecret,
  sendWebhookTest,
  updateWebhook,
  webhookDeliveriesQuery,
  webhooksShellQuery,
  type Webhook,
  type WebhookAttempt,
  type WebhookDelivery,
  type WebhookEventGroup,
  type WebhookInput,
  type WebhooksShell,
} from '../../api/settingsWebhooks'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './WebhooksPage.css'

/**
 * Settings -> INTEGRARE -> Webhooks (nou, numai in v4; cerinta CEC R12).
 *
 * Abonamentele firmei la evenimentele platformei, si jurnalul livrarilor lor.
 * Livrarea o face cronul ph4 `platform/webhooks`, la un minut - de-aia "Send
 * test event" si "Resend" pun in coada, nu trimit pe loc, si o spun.
 *
 * SECRETUL SE ARATA O SINGURA DATA, in caseta de sus, dupa facere sau rotatie.
 * Ecranul nu se reincarca peste el: in baza sta cifrat si nu se mai scoate.
 */

const TITLE = 'Webhooks'

function failed(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback
}

/** `2026-10-12 09:30:00` -> `2026-10-12 09:30`. */
export function shortStamp(value: string): string {
  return String(value ?? '').substring(0, 16)
}

/** Formularul gol, sau cel al unui abonament existent (antetele fara valori). */
export function formFor(hook: Webhook | null): WebhookInput {
  return hook === null
    ? { url: '', description: '', events: [], headers: [], active: true, scope: 'company' }
    : {
        url: hook.url,
        description: hook.description,
        events: [...hook.events],
        headers: hook.headerNames.map((name) => ({ name, value: '' })),
        active: hook.active,
      }
}

/** Eticheta starii unei livrari, cum o citeste omul. */
export function deliveryLabel(d: WebhookDelivery): string {
  switch (d.status) {
    case 'success':
      return 'Delivered'
    case 'failed':
      return 'Failed'
    case 'cancelled':
      return 'Cancelled'
    case 'sending':
      return 'Sending'
    default:
      return d.attempts > 0 ? `Retrying (attempt ${d.attempts + 1} at ${shortStamp(d.nextAttemptAt)})` : 'Queued'
  }
}

/** JSON-ul payload-ului, frumos; textul ca atare daca nu e JSON. */
export function pretty(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2)
  } catch {
    return payload
  }
}

function FreshSecret({ value, onClose }: { value: string; onClose: () => void }) {
  const copy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      void navigator.clipboard.writeText(value).then(() => toast('The secret is in your clipboard.', TITLE))
    }
  }

  return (
    <div className="wh-fresh" id="whFresh">
      <h4>
        <i className="fas fa-key"></i> Signing secret
      </h4>
      <p className="wh-mono" id="whFreshSecret">
        {value}
      </p>
      <button className="btn btn-default btn-xs" type="button" onClick={copy}>
        <i className="far fa-copy"></i> Copy
      </button>{' '}
      <button className="btn btn-link btn-xs" type="button" onClick={onClose}>
        I have saved it
      </button>
      <p className="wh-note" style={{ margin: '10px 0 0' }}>
        Shown once. It is stored encrypted and cannot be shown again; whoever loses it rotates it. Every delivery carries{' '}
        <code>X-OD-Signature: t=&lt;time&gt;,v1=&lt;HMAC-SHA256&gt;</code> - see the documentation (docs/webhooks.md).
      </p>
    </div>
  )
}

export function EventPicker({
  catalog,
  value,
  onChange,
  disabled = false,
}: {
  catalog: WebhookEventGroup[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  const toggle = (event: string, on: boolean) =>
    onChange(on ? [...value.filter((e) => e !== event), event] : value.filter((e) => e !== event))
  const all = catalog.flatMap((g) => g.events.map((e) => e.event))

  return (
    <div className="wh-events">
      <button
        className="btn btn-link btn-xs"
        type="button"
        disabled={disabled}
        onClick={() => onChange(value.length === all.length ? [] : all)}
      >
        {value.length === all.length ? 'Clear all' : 'Select all'}
      </button>
      {catalog.map((group) => (
        <fieldset className="wh-events__group" key={group.group}>
          <legend>{group.group}</legend>
          {group.events.map((one) => (
            <label className="wh-events__item" key={one.event}>
              <input
                type="checkbox"
                name="events[]"
                value={one.event}
                checked={value.includes(one.event)}
                disabled={disabled}
                onChange={(e) => toggle(one.event, e.target.checked)}
              />{' '}
              {one.label} <span className="wh-mono wh-note">{one.event}</span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

function WebhookForm({
  page,
  hook,
  busy,
  onSave,
  onCancel,
}: {
  page: WebhooksShell
  hook: Webhook | null
  busy: boolean
  onSave: (input: WebhookInput) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<WebhookInput>(() => formFor(hook))
  const set = (patch: Partial<WebhookInput>) => setForm({ ...form, ...patch })
  const setHeader = (i: number, patch: Partial<{ name: string; value: string }>) =>
    set({ headers: form.headers.map((h, j) => (j === i ? { ...h, ...patch } : h)) })

  return (
    <form
      className="wh-card wh-form"
      id="whForm"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(form)
      }}
    >
      <h4>{hook === null ? 'New webhook' : 'Edit webhook'}</h4>
      <label className="wh-field">
        <span>Payload URL (HTTPS)</span>
        <input
          className="form-control"
          name="url"
          type="url"
          required
          placeholder="https://example.com/optimumdesk/webhook"
          value={form.url}
          onChange={(e) => set({ url: e.target.value })}
        />
      </label>
      <label className="wh-field">
        <span>Description</span>
        <input
          className="form-control"
          name="description"
          maxLength={500}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </label>
      {hook === null && page.canProviderWide ? (
        <label className="wh-field">
          <span>Scope</span>
          <select
            className="form-control"
            name="scope"
            value={form.scope}
            onChange={(e) => set({ scope: e.target.value === 'provider' ? 'provider' : 'company' })}
          >
            <option value="company">This company</option>
            <option value="provider">All companies of the service provider</option>
          </select>
        </label>
      ) : null}
      <div className="wh-field">
        <span>Events</span>
        <EventPicker catalog={page.eventCatalog} value={form.events} onChange={(events) => set({ events })} disabled={busy} />
      </div>
      <div className="wh-field">
        <span>Extra headers (optional, sent with every delivery; values are stored encrypted)</span>
        {form.headers.map((h, i) => (
          <div className="wh-header-row" key={i}>
            <input
              className="form-control"
              aria-label="Header name"
              placeholder="Authorization"
              value={h.name}
              onChange={(e) => setHeader(i, { name: e.target.value })}
            />
            <input
              className="form-control"
              aria-label="Header value"
              type="password"
              autoComplete="off"
              placeholder={hook !== null && hook.headerNames.includes(h.name) ? '(unchanged)' : 'value'}
              value={h.value}
              onChange={(e) => setHeader(i, { value: e.target.value })}
            />
            <button
              className="btn btn-link btn-xs"
              type="button"
              aria-label="Remove header"
              onClick={() => set({ headers: form.headers.filter((_, j) => j !== i) })}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        ))}
        {form.headers.length < 10 ? (
          <button
            className="btn btn-default btn-xs"
            type="button"
            onClick={() => set({ headers: [...form.headers, { name: '', value: '' }] })}
          >
            <i className="fas fa-plus"></i> Add header
          </button>
        ) : null}
      </div>
      <label className="wh-check">
        <input type="checkbox" name="active" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />{' '}
        Active
      </label>
      <div>
        <button className="btn btn-info btn-sm" id="whSave" type="submit" disabled={busy}>
          <i className="fas fa-check"></i> {hook === null ? 'Create webhook' : 'Save'}
        </button>{' '}
        <button className="btn btn-default btn-sm" type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function AttemptView({ attempt }: { attempt: WebhookAttempt }) {
  return (
    <div className="wh-attempt">
      <div>
        <strong>Attempt {attempt.attempt}</strong> <span className="wh-note">{attempt.startedAt}</span>
        {' - '}
        {attempt.responseCode !== null ? `HTTP ${attempt.responseCode}` : 'no response'}
        {attempt.durationMs !== null ? `, ${attempt.durationMs} ms` : ''}
        {attempt.resolvedIp !== '' ? <span className="wh-note"> ({attempt.resolvedIp})</span> : null}
      </div>
      {attempt.error !== '' ? <div className="text-danger">{attempt.error}</div> : null}
      <details>
        <summary>Request headers</summary>
        <pre className="wh-pre">{attempt.requestHeaders}</pre>
      </details>
      {attempt.responseBody !== '' ? (
        <details>
          <summary>Response (first 2000 bytes)</summary>
          <pre className="wh-pre">{attempt.responseBody}</pre>
        </details>
      ) : null}
    </div>
  )
}

function DeliveryDetails({ hookId, delivery }: { hookId: number; delivery: number }) {
  const detail = useQuery({
    queryKey: ['settings', 'webhooks', hookId, 'delivery', delivery],
    queryFn: () => getWebhookDelivery(hookId, delivery),
  })
  if (!detail.data) return <Spinner />

  return (
    <div className="wh-detail">
      <div className="wh-note">
        Event id <span className="wh-mono">{detail.data.delivery.eventId}</span>
      </div>
      <details open>
        <summary>Payload</summary>
        <pre className="wh-pre">{pretty(detail.data.delivery.payload)}</pre>
      </details>
      {detail.data.attempts.length === 0 ? (
        <p className="wh-note">Not attempted yet.</p>
      ) : (
        detail.data.attempts.map((a) => <AttemptView attempt={a} key={a.attempt + a.startedAt} />)
      )}
    </div>
  )
}

function Deliveries({ hook, canWrite }: { hook: Webhook; canWrite: boolean }) {
  const query = useQueryClient()
  const list = useQuery(webhookDeliveriesQuery(hook.id))
  const [open, setOpen] = useState<number | null>(null)

  const resend = (id: number) => {
    void resendWebhookDelivery(hook.id, id)
      .then(() => {
        toast('Queued again. It leaves within a minute.', TITLE)
        void query.invalidateQueries({ queryKey: ['settings', 'webhooks', hook.id] })
      })
      .catch((error: unknown) => toast(failed(error, 'The delivery could not be queued.'), TITLE, 'error'))
  }

  if (!list.data) return <Spinner />
  if (list.data.deliveries.length === 0) return <p className="wh-note">No deliveries yet.</p>

  return (
    <table className="table table-condensed wh-deliveries">
      <thead>
        <tr>
          <th>Created</th>
          <th>Event</th>
          <th>Status</th>
          <th>Code</th>
          <th>Duration</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {list.data.deliveries.map((d) => (
          <DeliveryRow
            key={d.id}
            d={d}
            hookId={hook.id}
            open={open === d.id}
            onToggle={() => setOpen(open === d.id ? null : d.id)}
            onResend={canWrite && d.status !== 'sending' ? () => resend(d.id) : null}
          />
        ))}
      </tbody>
    </table>
  )
}

function DeliveryRow({
  d,
  hookId,
  open,
  onToggle,
  onResend,
}: {
  d: WebhookDelivery
  hookId: number
  open: boolean
  onToggle: () => void
  onResend: (() => void) | null
}) {
  return (
    <>
      <tr className={`wh-delivery wh-delivery--${d.status}`}>
        <td>{shortStamp(d.createdAt)}</td>
        <td>
          <span className="wh-mono">{d.event}</span>
          {d.test ? <span className="label label-default wh-tag">test</span> : null}
        </td>
        <td>
          {deliveryLabel(d)}
          {d.lastError !== '' && d.status !== 'success' ? <div className="wh-note text-danger">{d.lastError}</div> : null}
        </td>
        <td>{d.responseCode ?? '-'}</td>
        <td>{d.durationMs !== null ? `${d.durationMs} ms` : '-'}</td>
        <td className="wh-actions">
          <button className="btn btn-link btn-xs" type="button" onClick={onToggle}>
            {open ? 'Hide' : 'Details'}
          </button>
          {onResend ? (
            <button className="btn btn-default btn-xs" type="button" onClick={onResend}>
              <i className="fas fa-redo"></i> Resend
            </button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={6}>
            <DeliveryDetails hookId={hookId} delivery={d.id} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

function WebhookCard({
  hook,
  page,
  busy,
  onEdit,
  onAct,
}: {
  hook: Webhook
  page: WebhooksShell
  busy: boolean
  onEdit: () => void
  onAct: (what: 'test' | 'rotate' | 'delete') => void
}) {
  const [showLog, setShowLog] = useState(false)
  const labels = new Map(page.eventCatalog.flatMap((g) => g.events.map((e) => [e.event, e.label] as const)))

  return (
    <div className="wh-card wh-hook" data-hook={hook.id}>
      <div className="wh-hook__head">
        <span className={`wh-dot ${hook.active ? 'wh-dot--on' : 'wh-dot--off'}`} aria-hidden="true"></span>
        <span className="wh-mono wh-hook__url">{hook.url}</span>
        {hook.scope === 'provider' ? <span className="label label-info wh-tag">all companies</span> : null}
        <span className="wh-spacer"></span>
        <span className="wh-note">{hook.active ? 'Active' : 'Inactive'}</span>
      </div>
      {hook.description !== '' ? <div>{hook.description}</div> : null}
      {hook.disabledReason !== '' && !hook.active ? (
        <div className="alert alert-warning wh-alert">
          <i className="fas fa-exclamation-triangle"></i> {hook.disabledReason}{' '}
          <span className="wh-note">({shortStamp(hook.disabledAt)})</span>
        </div>
      ) : null}
      <div className="wh-chips">
        {hook.events.map((e) => (
          <span className="wh-chip" key={e} title={e}>
            {labels.get(e) ?? e}
          </span>
        ))}
      </div>
      <div className="wh-note">
        Secret &hellip;{hook.secretHint}
        {hook.previousSecretUntil !== '' ? ` (previous secret still signs until ${shortStamp(hook.previousSecretUntil)})` : ''}
        {hook.headerNames.length > 0 ? ` · headers: ${hook.headerNames.join(', ')}` : ''}
        {hook.lastDeliveryAt !== '' ? ` · last delivery ${shortStamp(hook.lastDeliveryAt)}: ${hook.lastStatus}` : ' · no delivery yet'}
        {hook.consecutiveFailures > 0 ? ` · ${hook.consecutiveFailures} failed in a row` : ''}
      </div>
      <div className="wh-buttons">
        <button className="btn btn-default btn-xs" type="button" onClick={() => setShowLog(!showLog)}>
          <i className="fas fa-list"></i> {showLog ? 'Hide deliveries' : 'Deliveries'}
        </button>
        {page.canWrite ? (
          <>
            <button className="btn btn-default btn-xs" type="button" disabled={busy} onClick={() => onAct('test')}>
              <i className="fas fa-paper-plane"></i> Send test event
            </button>
            <button className="btn btn-default btn-xs" type="button" disabled={busy} onClick={onEdit}>
              <i className="fas fa-pen"></i> Edit
            </button>
            <button className="btn btn-default btn-xs" type="button" disabled={busy} onClick={() => onAct('rotate')}>
              <i className="fas fa-sync"></i> Rotate secret
            </button>
            <button className="btn btn-link btn-xs text-danger" type="button" disabled={busy} onClick={() => onAct('delete')}>
              <i className="fas fa-trash"></i> Delete
            </button>
          </>
        ) : null}
      </div>
      {showLog ? <Deliveries hook={hook} canWrite={page.canWrite} /> : null}
    </div>
  )
}

export function WebhooksPage() {
  const query = useQueryClient()
  const shell = useQuery(webhooksShellQuery)
  const [live, setLive] = useState<WebhooksShell | null>(null)
  const [secret, setSecret] = useState('')
  const [editing, setEditing] = useState<Webhook | 'new' | null>(null)
  const [busy, setBusy] = useState(false)

  const page = live ?? shell.data ?? null
  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page) return <Spinner />

  const refresh = (next?: WebhooksShell) => {
    if (next) setLive(next)
    void query.invalidateQueries({ queryKey: ['settings', 'webhooks'] })
  }

  const save = (input: WebhookInput) => {
    setBusy(true)
    const call = editing === 'new' || editing === null ? createWebhook(input) : updateWebhook(editing.id, input)
    void call
      .then((next) => {
        setBusy(false)
        setEditing(null)
        if ('secret' in next && typeof next.secret === 'string') setSecret(next.secret)
        refresh(next)
        toast(editing === 'new' ? 'Webhook created. Copy the secret now.' : 'Webhook saved.', TITLE)
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error, 'The webhook could not be saved.'), TITLE, 'error')
      })
  }

  const act = (hook: Webhook, what: 'test' | 'rotate' | 'delete') => {
    if (what === 'delete' && !window.confirm('Delete this webhook? Nothing more is sent to it.')) return
    if (what === 'rotate' && !window.confirm('Generate a new secret? The current one keeps signing for 24 hours.')) return
    setBusy(true)
    const done = (message: string) => {
      setBusy(false)
      toast(message, TITLE)
    }
    const fail = (error: unknown) => {
      setBusy(false)
      toast(failed(error, 'The action failed.'), TITLE, 'error')
    }
    if (what === 'test') {
      void sendWebhookTest(hook.id)
        .then(() => {
          done('Test event queued. It leaves within a minute; see Deliveries.')
          void query.invalidateQueries({ queryKey: ['settings', 'webhooks', hook.id] })
        })
        .catch(fail)
    } else if (what === 'rotate') {
      void rotateWebhookSecret(hook.id)
        .then((next) => {
          setSecret(next.secret)
          refresh(next)
          done('New secret generated. Copy it now.')
        })
        .catch(fail)
    } else {
      void deleteWebhook(hook.id)
        .then((next) => {
          refresh(next)
          done('Webhook deleted.')
        })
        .catch(fail)
    }
  }

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {page.tabs.length > 0 ? <li className="menu-header">{page.tabs[0].group}</li> : null}
          {page.tabs.map((tab) => (
            <li className="menu-tab active" key={tab.id}>
              <a id={tab.id} href={`/app/${tab.href}`} onClick={(e) => e.preventDefault()}>
                <i className={tab.icon} aria-hidden="true"></i> {tab.label}
              </a>
            </li>
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        <div id="content" className="wh-page">
          <div className="wh-title">
            <h3>Webhooks</h3>
            <span className="wh-spacer"></span>
            {page.canWrite && page.ready && page.cryptoReady && editing === null ? (
              <button className="btn btn-info btn-sm" id="whNew" type="button" onClick={() => setEditing('new')}>
                <i className="fas fa-plus"></i> New webhook
              </button>
            ) : null}
          </div>
          <p className="wh-note">
            The platform sends a signed JSON POST to your URL when an event happens. Deliveries are retried after 1 min, 5
            min, 30 min, 2 h and 12 h; a webhook whose deliveries keep failing is turned off and the company admins get an
            email. Internal and private addresses are refused.
          </p>

          {!page.ready ? (
            <div className="alert alert-warning">Webhooks are not installed on this platform yet (the database migration has not run).</div>
          ) : null}
          {page.ready && !page.cryptoReady ? (
            <div className="alert alert-warning">
              Webhook secrets cannot be stored on this installation (DEFUSE_CRYPT_PASS is not set).
            </div>
          ) : null}

          {secret !== '' ? <FreshSecret value={secret} onClose={() => setSecret('')} /> : null}

          {editing !== null ? (
            <WebhookForm
              key={editing === 'new' ? 'new' : editing.id}
              page={page}
              hook={editing === 'new' ? null : editing}
              busy={busy}
              onSave={save}
              onCancel={() => setEditing(null)}
            />
          ) : null}

          {page.webhooks.length === 0 && editing === null ? (
            <div className="wh-card">
              <p className="wh-note" style={{ margin: 0 }}>
                No webhooks yet.
              </p>
            </div>
          ) : null}

          {page.webhooks.map((hook) => (
            <WebhookCard
              key={hook.id}
              hook={hook}
              page={page}
              busy={busy}
              onEdit={() => setEditing(hook)}
              onAct={(what) => act(hook, what)}
            />
          ))}
        </div>
      </div>
    </>
  )
}
